/**
 * The fan page's fresh read of "has ordering been paused?".
 *
 * Since kiosks took over ordering (#146), the pause switch staff use (Orders
 * hub) lives on the serving kiosk's operator_settings, and that is what
 * create-payment-intent enforces. club_settings.ordering_paused is a legacy
 * copy no screen writes any more; reading it locked IF Gnistan's fans out on
 * 22 Oct 2026 ("orders temporarily paused") while the kiosk was open. So the
 * read follows the kiosk, and falls back to the club only when no kiosk
 * serves the seat.
 */

/** The slice of the Supabase client this read needs (structural, so tests can fake it). */
export interface PauseReadClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  from(table: string): any;
}

export interface PauseReadTarget {
  /** The kiosk serving the seat (get_fan_page_data's `operator.id`), if any. */
  operatorId: string | null | undefined;
  /** The tenant club of the page. */
  clubId: string | null | undefined;
}

/** true / false when the switch could be read; null when there is nothing to read or the read failed. */
export async function readOrderingPaused(client: PauseReadClient, target: PauseReadTarget): Promise<boolean | null> {
  const source = target.operatorId
    ? { table: 'operator_settings', column: 'operator_id', id: target.operatorId }
    : target.clubId
      ? { table: 'club_settings', column: 'club_id', id: target.clubId }
      : null;
  if (!source) return null;

  const { data, error } = (await client
    .from(source.table)
    .select('ordering_paused')
    .eq(source.column, source.id)
    .maybeSingle()) as { data: { ordering_paused?: boolean | null } | null; error: unknown };
  if (error || !data) return null;
  return !!data.ordering_paused;
}
